import { redirect } from "@tanstack/react-router";
import { clsx, type ClassValue } from "clsx"
import { DateRange } from "react-day-picker";
import { twMerge } from "tailwind-merge"
import { z } from "zod";

import {
  DEFAULT_CARD_ERROR_MESSAGE, errorMessages,
} from "@/i18n";
import { ApiError } from "@/lib/api";
import { formatDate } from "@/lib/filters/date/date-utils";
import { clearTokenData, isTermsAccepted, isTwoFactorInProgress } from "@/lib/token/token";
import { UnableToReadTokenError } from "@/lib/types/errors";
import Firm from "@/types/firm"
import { Identity } from "@/types/identity"
import { FetchParams } from "@/types/search";

export function checkTwoFactorStateAndRedirect(): void {
  if (!isTwoFactorInProgress()){
    return;
  }

  // Grab current URL params
  const params = new URLSearchParams(window.location.search);

  // Set redirect param, fallback to full path+search+hash
  params.set(
    "redirect", 
    params.get("redirect") 
      ?? `${window.location.pathname}${window.location.search}${window.location.hash}`
  );

  // Convert to plain Record<string, string>
  const searchRecord: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    searchRecord[key] = value;
  }

  throw redirect({
    to: "/two-factor",
    search: searchRecord,
  });
}

/**
 * Reads the terms claim, turning an unreadable token into a clean bounce to login (VFX-3096).
 *
 * Letting UnableToReadTokenError propagate does reach defaultOnCatch in main.tsx, which clears
 * storage and redirects — but the router renders the errored match before it calls onCatch, so the
 * user gets a flash of the generic "Something went wrong" component first. Redirecting from inside
 * beforeLoad skips the render entirely. Clearing is not optional: /login bounces anyone still
 * holding a token straight back to /, so leaving the unreadable one in place would loop.
 */
function readTermsAccepted(): boolean {
  try {
    return isTermsAccepted();
  } catch (error) {
    if (!(error instanceof UnableToReadTokenError)) {
      throw error;
    }

    clearTokenData();

    throw redirect({ to: '/login' });
  }
}

/**
 * Sends an un-accepted user to the terms gate, stashing where they were headed (VFX-3096).
 *
 * Takes the href rather than reading window.location: the router's location is already relative to
 * the /v2 basepath, which is what the redirect target needs.
 */
export function checkTermsStateAndRedirect(href: string): void {
  if (readTermsAccepted()) {
    return;
  }

  throw redirect({
    to: '/terms',
    search: { redirect: href },
  });
}

/**
 * The inverse: keeps an already-accepted user off the terms gate, sending them on to whatever the
 * gate stashed, or the dashboard.
 */
export function redirectIfTermsAccepted(): void {
  if (!readTermsAccepted()) {
    return;
  }

  throw redirect({
    to: new URLSearchParams(window.location.search).get('redirect') || '/',
  });
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function capitalise(string: string): string {
  return string.replace(/\b\w/g, l => l.toUpperCase())
}

export function extractFirmsFromIdentity(
  identity: Pick<Identity, 'network' | 'networkFirms' | 'allowedFirms' | 'firmSubgroups'> | null
): { firms: Firm[], subGroups: Firm[] } {
  if (identity?.network !== null) {
    const networkFirms = identity?.networkFirms.sort((firmA, firmB) => firmA.label < firmB.label ? -1 : 1)

    return {
      firms: networkFirms?.map(nwf => ({ name: nwf.label, id: nwf.value })) || [],
      subGroups: []
    }
  }

  const firmSorter = (firmA: Firm, firmB: Firm) => firmA.name < firmB.name ? -1 : 1

  return {
    firms: Array.isArray(identity?.allowedFirms) ? identity.allowedFirms.sort(firmSorter) : [],
    subGroups: Array.isArray(identity?.firmSubgroups) ? identity.firmSubgroups.sort(firmSorter) : [],
  }
}

export function numberToHumanReadableString(value: number) {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

export function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return ((...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  }) as T;
}

export function isCurrentPage(path: string) {
  const pathName = window.location.pathname.replace('/v2', '')
  return pathName === path
}

export const hasApplicationFeature = (featureFlag: string) => {
  const applicationFeatureFlags = import.meta.env.VITE_APPLICATION_FEATURE_FLAGS_REPLACEME?.split(",") ?? [];
  return applicationFeatureFlags.includes(featureFlag)
}

export function canUserEditRiskAndOpportunities(identity: Identity, hasRoleToEditRisksAndOpportunities: boolean) {
  const network = identity.network !== null;
  const firmViewOnNetworkPortal = isFirmViewOnNetworkPortal(identity);

  if (!network) {
    return !identity?.canNetworkSetRiskAndOpportunitiesVisibility && hasRoleToEditRisksAndOpportunities;
  }

  if (firmViewOnNetworkPortal) {
    return false;
  }

  if (!identity?.canNetworkSetRiskAndOpportunitiesVisibility) {
    return false;
  }

  return hasRoleToEditRisksAndOpportunities;
}

export function isFirmViewOnNetworkPortal(identity: Identity): boolean {
  return identity.network !== null && !(identity.currentFirm === null || identity.currentFirm === undefined)
}

export function stringifySearchParams(params: Record<string, any>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value?.toString() ?? ''])
  );
}

export function getErrorMessage(error: Error | ApiError | null) {
  if (!error) {
    return ''
  }

  if (error instanceof ApiError && error?.statusCode && errorMessages[error.statusCode]) {
    return errorMessages[error.statusCode]
  }

  return DEFAULT_CARD_ERROR_MESSAGE
}

export function formatRank(rank: number) {
  const multiplier = 100
  let adjusted = 0

  if (rank !== 0) {
    // Round down positives, but clamp no lower than rank (no overshoot)
    // Round up negatives, but clamp no higher than rank (no undershoot)
    adjusted = rank > 0
      ? Math.min(Math.floor(rank * multiplier) / multiplier + 0.01, rank)
      : Math.max(Math.ceil(rank * multiplier) / multiplier - 0.01, rank)
  }

  return Number.isInteger(adjusted)
    ? adjusted.toString()
    : adjusted.toFixed(2) // e.g. "3.10", "3.33", "99.99"
}

export function formatRankWithoutComparison(rank: number) {
  const multiplier = 100
  let adjusted = 0

  if (rank !== 0) {
    // Round down positives, but clamp no lower than rank (no overshoot)
    // Round up negatives, but clamp no higher than rank (no undershoot)
    adjusted = rank > 0
      ? Math.min(Math.ceil(rank * multiplier) / multiplier + 0.01, rank)
      : Math.max(Math.ceil(rank * multiplier) / multiplier - 0.01, rank)
  }

  return Number.isInteger(adjusted)
    ? adjusted.toString()
    : Math.ceil(adjusted).toString()
}

export const buildQueryParamsStringWithFilters = (fetchParams: Partial<FetchParams>, date: DateRange, serviceType: number | null): string => {
  const { limit, offset, order_by, ...restFilters } = fetchParams

  const queryParams = new URLSearchParams()

  if (limit) queryParams.append("limit", String(limit))
  if (offset !== undefined) queryParams.append("offset", String(offset))

  const orderByCol = order_by?.column ?? 'id'
  const orderByDirection = order_by?.direction ?? 'DESC'
  queryParams.append(`order_by[${orderByCol}]`, orderByDirection)

  const to = formatDate(date.to, 'dd/MM/yyyy')
  const from = formatDate(date.from, 'dd/MM/yyyy')

  if (to) queryParams.append(`filters[to]`, to)
  if (from) queryParams.append(`filters[from]`, from)
  if (serviceType) queryParams.append(`filters[service_type_id]`, String(serviceType))


  Object.entries(restFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(`filters[${key}]`, String(value))
    }
  })

  return queryParams.toString()
}

export const buildQueryParamsString = (fetchParams: Partial<FetchParams>, date: DateRange, serviceType: number | null): string => {
  const { limit, offset, order_by, ...restFilters } = fetchParams

  const queryParams = new URLSearchParams()

  if (limit) queryParams.append("limit", String(limit))
  if (offset !== undefined) queryParams.append("offset", String(offset))

  const orderByCol = order_by?.column ?? 'id'
  const orderByDirection = order_by?.direction ?? 'DESC'
  queryParams.append(`order_by[${orderByCol}]`, orderByDirection)

  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  if (to) queryParams.append(`to`, to)
  if (from) queryParams.append(`from`, from)
  if (serviceType) queryParams.append(`service_type_id`, String(serviceType))

  Object.entries(restFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(`${key}`, String(value))
    }
  })

  return queryParams.toString()
}

export function validateSearchWithDefaults<T extends z.ZodTypeAny>(
  schema: T,
  input: unknown
): z.infer<T> {
  const result = schema.safeParse(input);
  return result.success ? result.data : schema.parse({});
}

export default function uploadFile(dataFile: File): Promise<{
  rawFile: string
  src: string
  title: string
  dataURL: string | ArrayBuffer | null
}> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.readAsDataURL(dataFile)

    fileReader.onloadend = () => {
      resolve({
        rawFile: '{}',
        src: '',
        title: dataFile.name,
        dataURL: fileReader.result,
      })
    }

    fileReader.onerror = (err) => reject(err)
  })
}


export function capitalizeFirstLetterAndRemoveUnderscore(str: string) {
  const noUnderscores = removeUnderscore(str.toLowerCase())
  return noUnderscores.charAt(0).toUpperCase() + noUnderscores.slice(1)
}

export function removeUnderscore(str: string) {
  return  str.replace(/_/g, ' ')
}
